import { inject, Injectable, InjectionToken } from '@angular/core';
import { TaskFormHandler } from './task-form-handler.interface';

/**
 * InjectionToken for registering multiple TaskFormHandler implementations.
 * Use multi: true when providing handlers in module providers.
 */
export const TASK_FORM_HANDLERS = new InjectionToken<TaskFormHandler[]>('TASK_FORM_HANDLERS');

/**
 * Registry service for managing and retrieving task form handlers.
 * This service uses the Strategy pattern to dynamically select the appropriate
 * handler based on the form key.
 */
@Injectable({ providedIn: 'root' })
export class TaskFormHandlerRegistry {
  private readonly handlers = inject(TASK_FORM_HANDLERS, { optional: true }) ?? [];

  /**
   * Get the appropriate handler for the given form key.
   * Returns null if no handler is found.
   */
  getHandler(formKey: string | null | undefined): TaskFormHandler | null {
    if (!formKey) {
      return this.handlers.find(h => h.formKey === 'default') ?? null;
    }
    return this.handlers.find(h => h.canHandle(formKey)) ?? null;
  }

  /**
   * Check if a handler exists for the given form key
   */
  hasHandler(formKey: string | null | undefined): boolean {
    return this.getHandler(formKey) !== null;
  }

  /**
   * Get all registered handlers (useful for debugging)
   */
  getAllHandlers(): TaskFormHandler[] {
    return [...this.handlers];
  }
}
